import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API } from 'aws-amplify';
import swal from '@sweetalert/with-react';

import Button from '../../Button';
import Dropdown from '../../Dropdown';
import ScheduleGrid from '../ScheduleGrid';
import ScheduleGridItem from '../ScheduleGridItem';
import EditBlock from '../modals/edit_block';

import { formatTime, createUniqueObjectID, getData, cloneObj } from '../../../js/Helpers';
import { buildDaySchedule, calculateTimeGridColumnStartEnd, getTimeGridConflictKeys, timesOverlap } from '../utils/scheduleGridUtils';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faTimes, faCheckSquare, faSquare } from '@fortawesome/free-solid-svg-icons';

import '../../../css/magnetboard.css';
import '../../../css/elementary/manage_homerooms.css';

const DEFAULT_TEACHER_TAG_COLOR = 'bright-blue';
const TEACHER_TAG_COLOR_HEX_MAP = {
	'red': '#c0392b',
	'bright-red': '#e74c3c',
	'orange': '#e67e22',
	'dark-orange': '#d35400',
	'yellow': '#f39c12',
	'bright-yellow': '#f1c40f',
	'green': '#27ae60',
	'bright-green': '#2ecc71',
	'turquoise': '#16a085',
	'blue': '#2980b9',
	'bright-blue': '#3498db',
	'dark-blue': '#092745',
	'dark-blue-gray': '#092745',
	'pastel-dark-blue-gray': '#678aae',
	'purple': '#8e44ad',
	'bright-purple': '#9b59b6',
	'pastel-light-green': '#55efc4',
	'pastel-green': '#00b894',
	'pastel-light-turquoise': '#81ecec',
	'pastel-turquoise': '#00cec9',
	'pastel-light-blue': '#74b9ff',
	'pastel-blue': '#0984e3',
	'pastel-light-purple': '#a29bfe',
	'dark-purple': '#474ccb',
	'dark-purple-gray': '#56588b',
	'pastel-dark-purple': '#6c5ce7',
	'pastel-light-yellow': '#ffeaa7',
	'pastel-yellow': '#fdcb6e',
	'pastel-light-orange': '#fab1a0',
	'pastel-orange': '#e17055',
	'pastel-brown': '#bc9e82',
	'dark-brown': '#695848',
	'light-gray': '#ecf0f1',
	'gray': '#cad1d2',
	'dark-gray': '#7f8c8d',
	'white': '#ffffff',
	'elem-tag-cool-01': '#0b6e4f',
	'elem-tag-cool-02': '#1f7a8c',
	'elem-tag-cool-03': '#2a628f',
	'elem-tag-cool-04': '#3b4cc0',
	'elem-tag-cool-05': '#5e60ce',
	'elem-tag-cool-06': '#4361ee',
	'elem-tag-cool-07': '#3a86ff',
	'elem-tag-cool-08': '#0077b6',
	'elem-tag-cool-09': '#0096c7',
	'elem-tag-cool-10': '#00a8a8',
	'elem-tag-cool-11': '#1b9e77',
	'elem-tag-cool-12': '#2d6a4f',
	'elem-tag-cool-13': '#3a5a40',
	'elem-tag-cool-14': '#4a4e69',
	'elem-tag-cool-15': '#5f0f99',
	'elem-tag-cool-16': '#6a4c93',
	'elem-tag-cool-17': '#4f5d75',
	'elem-tag-cool-18': '#3d405b',
	'elem-tag-cool-19': '#264653',
	'elem-tag-cool-20': '#2f4858',
	'elem-tag-cool-21': '#577590',
	'elem-tag-cool-22': '#277da1',
	'elem-tag-cool-23': '#4d908e',
	'elem-tag-cool-24': '#43aa8b'
};
const VALID_TEACHER_TAG_COLORS = new Set(Object.keys(TEACHER_TAG_COLOR_HEX_MAP));

const displayTeacherName = (teacher) => {
	if(!teacher) return '';
	const firstName = teacher.first_name ? `${teacher.first_name}`.trim() : '';
	const lastName = teacher.name ? `${teacher.name}`.trim() : '';
	return `${lastName}, ${firstName}`.replace(/,\s*$/, '');
};

const isGradeManagedBlock = (block) => block && block.is_special !== '1' && block.is_pullout !== '1';
const getGradeBlockColor = (block) => {
	if(block.is_lunch === '1') return 'pastel-yellow';
	if(block.is_special === '1') return 'green';
	if(block.is_pullout === '1') return 'orange';
	return 'dark-purple';
};
const tightenGradeBlockPosition = (position = {}) => {
	const positionKey = `${position.col_start || position.colStart}-${position.col_end || position.colEnd}`;
	const tightenedPositions = {
		'1-28': { col_start:'1', col_end:'30' },
		'32-60': { col_start:'30', col_end:'60' },
		'1-18': { col_start:'1', col_end:'20' },
		'21-39': { col_start:'20', col_end:'40' },
		'42-60': { col_start:'40', col_end:'60' },
		'1-10': { col_start:'1', col_end:'15' },
		'17-27': { col_start:'15', col_end:'30' },
		'33-43': { col_start:'30', col_end:'45' },
		'50-60': { col_start:'45', col_end:'60' }
	};

	return tightenedPositions[positionKey] ? { ...position, ...tightenedPositions[positionKey] } : position;
};
const createGradeBlockSignature = (block) => [
	`${block.course_id || ''}`,
	`${block.start_time || ''}`,
	`${block.end_time || ''}`,
	`${block.is_lunch || '0'}`,
	`${block.is_pullout || '0'}`,
	`${block.is_special || '0'}`
].join('__');

const getGradeBlockName = (block, courses = []) => {
	const courseInfo = courses.find((course) => course.course_id === block.course_id);
	if(courseInfo) return `${courseInfo.name}${courseInfo.course_code ? ` (${courseInfo.course_code})` : ''}`;
	if(block.block_name) return block.block_name;
	return block.is_lunch === '1' ? 'Lunch' : 'Untitled';
};

const normalizeTeacherTagColorClass = (tagColor) => {
	if(!tagColor) return DEFAULT_TEACHER_TAG_COLOR;
	let normalizedTagColor = `${tagColor}`.trim().toLowerCase().replace(/_/g, '-');
	if(normalizedTagColor === 'light-grey') normalizedTagColor = 'light-gray';
	if(normalizedTagColor === 'grey') normalizedTagColor = 'gray';
	if(normalizedTagColor === 'dark-grey') normalizedTagColor = 'dark-gray';
	if(normalizedTagColor === 'dark-blue-grey') normalizedTagColor = 'dark-blue-gray';
	if(normalizedTagColor === 'pastel-dark-blue-grey') normalizedTagColor = 'pastel-dark-blue-gray';
	if(normalizedTagColor === 'dark-purple-grey') normalizedTagColor = 'dark-purple-gray';
	return VALID_TEACHER_TAG_COLORS.has(normalizedTagColor) ? normalizedTagColor : DEFAULT_TEACHER_TAG_COLOR;
};

const getTeacherTagColorHex = (teacher) => {
	const normalizedTagColor = normalizeTeacherTagColorClass(teacher?.tag_color);
	return TEACHER_TAG_COLOR_HEX_MAP[normalizedTagColor] || TEACHER_TAG_COLOR_HEX_MAP[DEFAULT_TEACHER_TAG_COLOR];
};

const hexToRgb = (hexColor) => {
	const normalizedHex = `${hexColor || ''}`.trim().replace('#', '');
	if(normalizedHex.length !== 6) return null;
	const red = parseInt(normalizedHex.slice(0, 2), 16);
	const green = parseInt(normalizedHex.slice(2, 4), 16);
	const blue = parseInt(normalizedHex.slice(4, 6), 16);
	if([red, green, blue].some((value) => Number.isNaN(value))) return null;
	return { red, green, blue };
};

const getRgbaColor = (hexColor, alpha = 1) => {
	const rgbColor = hexToRgb(hexColor);
	if(!rgbColor) return hexColor;
	return `rgba(${rgbColor.red}, ${rgbColor.green}, ${rgbColor.blue}, ${alpha})`;
};

const getTeacherPillStyle = (teacher, selected = false) => {
	const tagColorHex = getTeacherTagColorHex(teacher);
	const borderColor = getRgbaColor(tagColorHex, selected ? 0.72 : 0.55);
	const backgroundColor = getRgbaColor(tagColorHex, selected ? 0.18 : 0.1);
	const checkboxBackground = selected ? tagColorHex : getRgbaColor(tagColorHex, 0.16);
	const checkboxBorderColor = selected ? tagColorHex : getRgbaColor(tagColorHex, 0.5);

	return {
		'--grade-pill-color': tagColorHex,
		'--grade-pill-checkbox-bg': checkboxBackground,
		'--grade-pill-checkbox-border': checkboxBorderColor,
		backgroundColor,
		borderColor,
		color: '#1f3b57',
		boxShadow: selected ? `0 10px 20px rgba(15, 36, 58, 0.14), inset 0 0 0 1px ${getRgbaColor(tagColorHex, 0.16)}` : `inset 0 0 0 1px ${getRgbaColor(tagColorHex, 0.08)}`
	};
};

export default function GradeBlocks({ subscreenLocked, subscreenRestricted, schoolInfo, scheduleInfo, periodsLoading, teachers = [], students = [], courses = [], setCourses = ()=>{}, classrooms = [], blocks = [], setBlocks = ()=>{}, initialHomeroomGroups = null, initialHomeroomGroupsLoading = false, handleMoveToNextScreen = ()=>{} }) {
	const [isLoading, setIsLoading] = useState(true);
	const [selectedGradeId, setSelectedGradeId] = useState(null);
	const [selectedTeacherIds, setSelectedTeacherIds] = useState([]);
	const [activeTeacherId, setActiveTeacherId] = useState(null);
	const [editorSelectedTeacherIds, setEditorSelectedTeacherIds] = useState([]);
	const [days, setDays] = useState([]);
	const [blockData, setBlockData] = useState(null);
	const [sidePanelClosing, setSidePanelClosing] = useState(false);
	const [editorPreviewState, setEditorPreviewState] = useState(null);
	const editorSelectionSeedRef = useRef(null);
	const editorBaseBlocksRef = useRef(null);
	const editorSaveCommittedRef = useRef(false);
	const previousBlockDataRef = useRef(null);

	const homeroomGroups = Array.isArray(initialHomeroomGroups) ? initialHomeroomGroups : [];

	const teacherById = useMemo(() => {
		const nextMap = new Map();
		teachers.forEach((teacher) => {
			nextMap.set(`${teacher.teacher_id}`, teacher);
		});
		return nextMap;
	}, [teachers]);

	const classroomById = useMemo(() => {
		const nextMap = new Map();
		classrooms.forEach((classroom) => {
			nextMap.set(`${classroom.classroom_id}`, classroom);
		});
		return nextMap;
	}, [classrooms]);

	const gradeOptions = useMemo(() => homeroomGroups.map((group) => ({
		value: `${group.id}`,
		display: group.label
	})), [homeroomGroups]);

	const selectedGrade = useMemo(() => homeroomGroups.find((group) => `${group.id}` === `${selectedGradeId}`) || null, [homeroomGroups, selectedGradeId]);

	const selectedGradeTeachers = useMemo(() => {
		if(!selectedGrade) return [];
		const gradeTeachers = (selectedGrade.teacher_ids || []).reduce((results, teacherId) => {
			const teacher = teacherById.get(`${teacherId}`);
			if(teacher) results.push(teacher);
			return results;
		}, []);
		return gradeTeachers.sort((teacherA, teacherB) => displayTeacherName(teacherA).localeCompare(displayTeacherName(teacherB)));
	}, [selectedGrade, teacherById]);

	const gradeTeacherIds = useMemo(() => selectedGradeTeachers.map((teacher) => `${teacher.teacher_id}`), [selectedGradeTeachers]);
	const selectedTeacherIdSet = useMemo(() => new Set(selectedTeacherIds.map((teacherId) => `${teacherId}`)), [selectedTeacherIds]);
	const selectedComparisonTeachers = useMemo(() => selectedGradeTeachers.filter((teacher) => selectedTeacherIdSet.has(`${teacher.teacher_id}`)), [selectedGradeTeachers, selectedTeacherIdSet]);
	const selectedTeacherCount = selectedComparisonTeachers.length;
	const allGradeTeachersSelected = gradeTeacherIds.length > 0 && selectedTeacherCount === gradeTeacherIds.length;
	const isComparisonMode = selectedTeacherCount > 1;
	const editorSelectedTeacherIdSet = useMemo(() => new Set(editorSelectedTeacherIds.map((teacherId) => `${teacherId}`)), [editorSelectedTeacherIds]);
	const previewTeacherId = useMemo(() => {
		if(activeTeacherId && selectedTeacherIdSet.has(`${activeTeacherId}`)) return `${activeTeacherId}`;
		return selectedTeacherIds[0] ? `${selectedTeacherIds[0]}` : null;
	}, [activeTeacherId, selectedTeacherIdSet, selectedTeacherIds]);
	const blockEditorSeedKey = blockData ? `${blockData.block_id || 'new'}-${blockData.block_day_id || blockData.day_number || 'day'}-${blockData.new ? 'new' : 'existing'}` : null;

	const selectedTeacher = useMemo(() => {
		if(!previewTeacherId) return null;
		return teacherById.get(`${previewTeacherId}`) || null;
	}, [previewTeacherId, teacherById]);

	const selectedTeacherStudents = useMemo(() => {
		if(!previewTeacherId) return [];
		return students.filter((student) => `${student.homeroom_teacher_id}` === `${previewTeacherId}`);
	}, [previewTeacherId, students]);

	const editorSelectedTeacherCount = editorSelectedTeacherIds.filter((teacherId) => gradeTeacherIds.includes(`${teacherId}`)).length;
	const editorHasSingleTeacherSelection = editorSelectedTeacherCount === 1;
	const editorSingleTeacher = useMemo(() => {
		if(!editorHasSingleTeacherSelection) return null;
		const editorTeacherId = editorSelectedTeacherIds.find((teacherId) => gradeTeacherIds.includes(`${teacherId}`));
		return editorTeacherId ? (teacherById.get(`${editorTeacherId}`) || null) : null;
	}, [editorHasSingleTeacherSelection, editorSelectedTeacherIds, gradeTeacherIds, teacherById]);
	const editorSelectedTeacherClassrooms = useMemo(() => {
		if(!editorSingleTeacher || !editorSingleTeacher.homeroom_classroom_id) return [];
		const classroom = classroomById.get(`${editorSingleTeacher.homeroom_classroom_id}`);
		return classroom ? [classroom] : [];
	}, [classroomById, editorSingleTeacher]);

	const createStudentIdsForTeacher = useCallback((teacherId) => students.reduce((results, student) => {
		if(`${student.homeroom_teacher_id}` === `${teacherId}`) results.push(student.student_id);
		return results;
	}, []), [students]);

	const getMatchingGradeBlocks = useCallback((templateBlock, sourceBlocks = blocks, sourceTeacherIds = gradeTeacherIds) => {
		if(!templateBlock || sourceTeacherIds.length === 0) return [];
		const normalizedTeacherIds = sourceTeacherIds.map((teacherId) => `${teacherId}`);
		return sourceBlocks.filter((block) => {
			if(!isGradeManagedBlock(block)) return false;
			if(!normalizedTeacherIds.includes(`${block.teacher_id}`) || !normalizedTeacherIds.includes(`${block.homeroom_teacher_id}`)) return false;
			return `${block.course_id || ''}` === `${templateBlock.course_id || ''}`
				&& `${block.start_time}` === `${templateBlock.start_time}`
				&& `${block.end_time}` === `${templateBlock.end_time}`
				&& `${block.is_lunch}` === `${templateBlock.is_lunch}`
				&& `${block.is_pullout}` === `${templateBlock.is_pullout}`
				&& `${block.is_special}` === `${templateBlock.is_special}`;
		});
	}, [blocks, gradeTeacherIds]);

	const cloneBlocks = useCallback((sourceBlocks = []) => cloneObj(sourceBlocks), []);

	const buildPreviewBlocksForTeacher = useCallback(({
		teacher,
		teacherId,
		desiredDayNumbers,
		blockDataCopy,
		classroomId = null,
		teacherOriginalBlocks = []
	}) => {
		const existingBlockId = teacherOriginalBlocks[0] ? `${teacherOriginalBlocks[0].block_id}` : null;
		const nextBlockId = existingBlockId || `${blockDataCopy.block_id || 'preview-block'}-${teacherId}`;
		const existingBlocksByDay = teacherOriginalBlocks.reduce((results, block) => {
			results.set(`${block.day_number}`, block);
			return results;
		}, new Map());
		const blockDays = desiredDayNumbers.map((dayNumber) => {
			const existingBlock = existingBlocksByDay.get(`${dayNumber}`);
			return {
				block_day_id: existingBlock ? existingBlock.block_day_id || null : `preview-day-${nextBlockId}-${dayNumber}`,
				day_number: `${dayNumber}`
			};
		});

		return desiredDayNumbers.map((dayNumber) => {
			const existingBlock = existingBlocksByDay.get(`${dayNumber}`);
			return {
				...(existingBlock ? cloneObj(existingBlock) : {}),
				block_id: nextBlockId,
				block_day_id: existingBlock ? existingBlock.block_day_id || null : `preview-day-${nextBlockId}-${dayNumber}`,
				course_id: blockDataCopy.course_id,
				block_name: blockDataCopy.block_name,
				classroom_id: classroomId,
				day_number: `${dayNumber}`,
				end_time: blockDataCopy.end_time,
				homeroom_teacher_id: teacherId,
				is_lunch: blockDataCopy.is_lunch,
				is_pullout: '0',
				is_special: '0',
				start_time: blockDataCopy.start_time,
				teacher_id: teacherId,
				student_list: createStudentIdsForTeacher(teacherId),
				locked_students: [],
				secondary_teachers: [],
				days: [...desiredDayNumbers],
				block_days: blockDays.map((blockDay) => ({ ...blockDay })),
				new: false
			};
		});
	}, [createStudentIdsForTeacher]);

	const displayBlocks = useMemo(() => {
		if(!blockData || !editorPreviewState || !editorPreviewState.blockDataCopy) return blocks;

		const baseBlocks = editorBaseBlocksRef.current ? cloneBlocks(editorBaseBlocksRef.current) : cloneBlocks(blocks);
		const { blockDataCopy, blockDataImmutable, affectedBlocks = [] } = editorPreviewState;
		const desiredDayNumbers = Array.from(new Set(affectedBlocks.map((affectedBlock) => `${affectedBlock.day_number}`)));
		const normalizedEditorTeacherIds = editorSelectedTeacherIds.filter((teacherId) => gradeTeacherIds.includes(`${teacherId}`));
		const selectedEditorTeacherIdSet = new Set(normalizedEditorTeacherIds.map((teacherId) => `${teacherId}`));
		const previewClassroomId = normalizedEditorTeacherIds.length === 1 ? (blockDataCopy.classroom_id || null) : null;
		const originalMatchingBlocks = (blockDataImmutable && blockDataImmutable.block_id)
			? getMatchingGradeBlocks(blockDataImmutable, baseBlocks, gradeTeacherIds)
			: [];
		const originalBlockDayKeys = new Set(originalMatchingBlocks.map((block) => `${block.block_day_id || `${block.block_id}-${block.day_number}`}`));
		const originalBlocksByTeacherId = originalMatchingBlocks.reduce((results, block) => {
			const teacherId = `${block.homeroom_teacher_id || block.teacher_id || ''}`;
			if(!results.has(teacherId)) results.set(teacherId, []);
			results.get(teacherId).push(block);
			return results;
		}, new Map());
		const previewBlocksWithoutEditedSection = baseBlocks.filter((block) => !originalBlockDayKeys.has(`${block.block_day_id || `${block.block_id}-${block.day_number}`}`));

		if(normalizedEditorTeacherIds.length === 0 || desiredDayNumbers.length === 0) return previewBlocksWithoutEditedSection;

		const teacherPreviewBlocks = selectedGradeTeachers.reduce((results, teacher) => {
			const teacherId = `${teacher.teacher_id}`;
			if(!selectedEditorTeacherIdSet.has(teacherId)) return results;
			return [
				...results,
				...buildPreviewBlocksForTeacher({
					teacher,
					teacherId,
					desiredDayNumbers,
					blockDataCopy,
					classroomId: previewClassroomId,
					teacherOriginalBlocks: originalBlocksByTeacherId.get(teacherId) || []
				})
			];
		}, []);

		return [...previewBlocksWithoutEditedSection, ...teacherPreviewBlocks];
	}, [blockData, blocks, buildPreviewBlocksForTeacher, cloneBlocks, editorPreviewState, editorSelectedTeacherIds, getMatchingGradeBlocks, gradeTeacherIds, selectedGradeTeachers]);

	const comparisonBlocks = useMemo(() => {
		if(selectedTeacherCount === 0) return [];
		return displayBlocks.filter((block) => {
			const homeroomTeacherId = `${block.homeroom_teacher_id || ''}`;
			const teachingTeacherId = `${block.teacher_id || ''}`;
			return selectedTeacherIdSet.has(homeroomTeacherId) || selectedTeacherIdSet.has(teachingTeacherId);
		});
	}, [displayBlocks, selectedTeacherCount, selectedTeacherIdSet]);
	const editableComparisonBlocks = useMemo(() => comparisonBlocks.filter((block) => isGradeManagedBlock(block)), [comparisonBlocks]);
	const underlayComparisonBlocks = useMemo(() => comparisonBlocks.filter((block) => !isGradeManagedBlock(block)), [comparisonBlocks]);

	const comparisonScheduleData = useMemo(() => {
		if(selectedTeacherCount === 0) return { sharedBlocks:[], floatingBlocks:[] };

		const getComparisonMembershipTeacherIds = (block) => {
			const membershipTeacherIds = new Set();
			const homeroomTeacherId = `${block.homeroom_teacher_id || ''}`;
			const teachingTeacherId = `${block.teacher_id || ''}`;
			if(selectedTeacherIdSet.has(homeroomTeacherId)) membershipTeacherIds.add(homeroomTeacherId);
			if(selectedTeacherIdSet.has(teachingTeacherId)) membershipTeacherIds.add(teachingTeacherId);
			return Array.from(membershipTeacherIds);
		};

		const groupedBlocks = new Map();
		editableComparisonBlocks.forEach((block) => {
			const dayNumber = `${block.day_number}`;
			const signature = createGradeBlockSignature(block);
			const groupKey = `${dayNumber}::${signature}`;
			const membershipTeacherIds = getComparisonMembershipTeacherIds(block);

			if(!groupedBlocks.has(groupKey))
			{
				groupedBlocks.set(groupKey, {
					signature,
					day_number: dayNumber,
					blocks: [],
					teacher_ids: new Set()
				});
			}

			const group = groupedBlocks.get(groupKey);
			group.blocks.push(block);
			membershipTeacherIds.forEach((teacherId) => group.teacher_ids.add(teacherId));
		});

		const sharedBlocks = [];

		groupedBlocks.forEach((group) => {
			const comparisonTeacherIds = selectedComparisonTeachers
				.map((teacher) => `${teacher.teacher_id}`)
				.filter((teacherId) => group.teacher_ids.has(teacherId));
			const representativeBlock = group.blocks.find((block) => {
				const homeroomTeacherId = `${block.homeroom_teacher_id || ''}`;
				const teachingTeacherId = `${block.teacher_id || ''}`;
				return `${previewTeacherId || ''}` === homeroomTeacherId || `${previewTeacherId || ''}` === teachingTeacherId;
			}) || group.blocks[0];

			sharedBlocks.push({
				...representativeBlock,
				comparison_shared_count: comparisonTeacherIds.length,
				comparison_signature: group.signature,
				comparison_teacher_ids: comparisonTeacherIds,
				comparison_teacher_total: selectedTeacherCount,
				comparison_teacher_badge: `${comparisonTeacherIds.length}/${selectedTeacherCount}`
			});
		});

		return { sharedBlocks, floatingBlocks:[] };
	}, [editableComparisonBlocks, previewTeacherId, selectedComparisonTeachers, selectedTeacherCount, selectedTeacherIdSet]);

	const buildComparisonDayBlocks = (dayNumber) => buildDaySchedule({
		items: comparisonScheduleData.sharedBlocks,
		dayNumber,
		lengthKey: 'block_length',
		filterItem: (block, tempDayNumber) => `${block.day_number}` === `${tempDayNumber}`
	});

	const buildComparisonFloatingBlocks = (dayNumber) => buildDaySchedule({
		items: comparisonScheduleData.floatingBlocks,
		dayNumber,
		lengthKey: 'block_length',
		filterItem: (block, tempDayNumber) => `${block.day_number}` === `${tempDayNumber}`
	});

	const buildComparisonUnderlayBlocks = (dayNumber) => buildDaySchedule({
		items: underlayComparisonBlocks,
		dayNumber,
		lengthKey: 'block_length',
		filterItem: (block, tempDayNumber) => `${block.day_number}` === `${tempDayNumber}`
	});

	const getCurrentDayConflictKeys = (dayNumber) => {
		const dayBlocks = buildDaySchedule({
			items: [...comparisonScheduleData.sharedBlocks, ...comparisonScheduleData.floatingBlocks],
			dayNumber,
			lengthKey: 'block_length'
		});
		return getTimeGridConflictKeys(dayBlocks, (block) => `${block.block_day_id || block.block_id}-${dayNumber}`);
	};

	const createNewBlockData = (teacherId, dayNumber, clickTimeRange = null) => {
		const actualTeacher = teacherById.get(`${teacherId}`);
		return {
			new: true,
			block_id: null,
			block_day_id: null,
			classroom_id: gradeTeacherIds.length === 1 && actualTeacher ? actualTeacher.homeroom_classroom_id : null,
			course_id: null,
			homeroom_teacher_id: `${teacherId}`,
			is_lunch: '0',
			is_pullout: '0',
			is_special: '0',
			start_time: clickTimeRange?.start_time || '00:00:00',
			end_time: clickTimeRange?.end_time || '00:00:00',
			teacher_id: `${teacherId}`,
			day_number: `${dayNumber}`,
			days: [`${dayNumber}`],
			block_days: [{block_day_id:null, day_number:`${dayNumber}`}],
			student_list: createStudentIdsForTeacher(`${teacherId}`),
			locked: '0',
			secondary_teachers: []
		};
	};

	const toggleEditBlock = (blockId, _blockType = null, teacherId = null, dayNumber = null, clickTimeRange = null) => {
		if(!blockId)
		{
			setBlockData(null);
			return;
		}

		if(!blockData)
		{
			editorBaseBlocksRef.current = cloneBlocks(blocks);
			editorSaveCommittedRef.current = false;
			setEditorPreviewState(null);
		}

		if(blockId === 'new')
		{
			setBlockData(createNewBlockData(teacherId, dayNumber, clickTimeRange));
			return;
		}

		const existingBlock = blocks.find((block) => block.block_id === blockId) || null;
		setBlockData(existingBlock);
	};

	const createNewBlock = (teacherId, dayNumber, clickTimeRange = null) => {
		toggleEditBlock('new', 'block', teacherId, dayNumber, clickTimeRange);
	};

	const handleSelectAllTeachers = () => {
		if(allGradeTeachersSelected)
		{
			setSelectedTeacherIds([]);
			setActiveTeacherId(null);
			return;
		}

		setSelectedTeacherIds([...gradeTeacherIds]);
		setActiveTeacherId((currentTeacherId) => {
			if(currentTeacherId && gradeTeacherIds.includes(`${currentTeacherId}`)) return `${currentTeacherId}`;
			return gradeTeacherIds[0] || null;
		});
	};

	const handleToggleTeacherSelection = (teacherId) => {
		const normalizedTeacherId = `${teacherId}`;
		setSelectedTeacherIds((currentTeacherIds) => {
			const teacherAlreadySelected = currentTeacherIds.includes(normalizedTeacherId);
			const nextTeacherIds = teacherAlreadySelected ? currentTeacherIds.filter((tempTeacherId) => tempTeacherId !== normalizedTeacherId) : [...currentTeacherIds, normalizedTeacherId];

			setActiveTeacherId((currentTeacherId) => {
				if(nextTeacherIds.length === 0) return null;
				if(!teacherAlreadySelected) return normalizedTeacherId;
				if(`${currentTeacherId || ''}` === normalizedTeacherId) return nextTeacherIds[nextTeacherIds.length - 1];
				return nextTeacherIds.includes(`${currentTeacherId || ''}`) ? `${currentTeacherId}` : nextTeacherIds[nextTeacherIds.length - 1];
			});

			return nextTeacherIds;
		});
	};

	const getMatchingGradeTeacherIds = useCallback((templateBlock, sourceBlocks = blocks, sourceTeacherIds = gradeTeacherIds) => {
		const normalizedTeacherIdSet = new Set(sourceTeacherIds.map((teacherId) => `${teacherId}`));
		return Array.from(new Set(
			getMatchingGradeBlocks(templateBlock, sourceBlocks, sourceTeacherIds)
				.map((block) => `${block.homeroom_teacher_id || block.teacher_id || ''}`)
				.filter((teacherId) => normalizedTeacherIdSet.has(teacherId))
		));
	}, [blocks, getMatchingGradeBlocks, gradeTeacherIds]);

	const refreshBlocksFromBackend = async () => {
		const refreshedBlocks = await getData('blocks', '/elementary/get-blocks', {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id}, true, 'elementary');
		setBlocks(refreshedBlocks);
	};

	const saveBlockDaysForBlock = async (targetBlock, remainingBlocks = []) => {
		const data = {
			school_id: schoolInfo.school_id,
			schedule_version_id: schoolInfo.current_schedule_version_id,
			affected_blocks: remainingBlocks.map((block) => ({
				block_day_id: block.block_day_id || null,
				day_number: `${block.day_number}`
			})),
			block_data: {
				block_id: targetBlock.block_id,
				course_id: targetBlock.course_id,
				classroom_id: targetBlock.classroom_id,
				homeroom_teacher_id: targetBlock.homeroom_teacher_id,
				teacher_id: targetBlock.teacher_id,
				start_time: targetBlock.start_time,
				end_time: targetBlock.end_time,
				is_lunch: targetBlock.is_lunch,
				is_pullout: targetBlock.is_pullout,
				is_special: targetBlock.is_special
			}
		};

		const apiName = process.env.REACT_APP_ELEM_ENDPOINT_NAME;
		const url = '/elementary/save-block';
		const myInit = {response:true, body:JSON.stringify(data)};

		await API.post(apiName, url, myInit);
	};

	const removeBlock = async (blockId, e = null, removeOptions = {}) => {
		const { scope = 'all', dayNumber = null, skipConfirm = false } = removeOptions;
		if(e && e.stopPropagation) e.stopPropagation();
		const targetBlock = blocks.find((block) => block.block_id === blockId && (!dayNumber || `${block.day_number}` === `${dayNumber}`)) || blocks.find((block) => block.block_id === blockId);
		if(!targetBlock) return;

		const options = {
			title: 'Are you sure?',
			icon: 'warning',
			dangerMode: true,
			buttons: {
				cancel: { text:'Cancel', value:false, visible:true, className:'gray-btn' },
				confirm: { text:'Yes', value:true, visible:true, className:'red-btn' },
			},
			content: <div><p>{scope === 'day' ? 'Are you sure you want to remove this day for the whole grade?' : 'Are you sure you want to remove this block for the whole grade?'}</p></div>
		};

		if(skipConfirm || await swal(options))
		{
			toggleEditBlock(null);

			try {
				const gradeBlocksToRemove = getMatchingGradeBlocks(targetBlock);

				if(scope === 'day' && dayNumber)
				{
					const gradeBlocksByLogicalBlock = gradeBlocksToRemove.reduce((results, block) => {
						const logicalBlockId = `${block.block_id}`;
						if(!results[logicalBlockId]) results[logicalBlockId] = [];
						results[logicalBlockId].push(block);
						return results;
					}, {});

					const blockIdsToDelete = [];
					for(const logicalBlockId of Object.keys(gradeBlocksByLogicalBlock))
					{
						const logicalBlocks = gradeBlocksByLogicalBlock[logicalBlockId];
						const remainingBlocks = logicalBlocks.filter((block) => `${block.day_number}` !== `${dayNumber}`);

						if(remainingBlocks.length === 0)
						{
							blockIdsToDelete.push(logicalBlockId);
						}
						else
						{
							await saveBlockDaysForBlock(logicalBlocks[0], remainingBlocks);
						}
					}

					if(blockIdsToDelete.length > 0)
					{
						const deleteData = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, block_ids:blockIdsToDelete};
						const apiName = process.env.REACT_APP_ELEM_ENDPOINT_NAME;
						const url = '/elementary/delete-block';
						const myInit = {response:true, body:JSON.stringify(deleteData)};
						await API.post(apiName, url, myInit);
					}
				}
				else
				{
					const blockIdsToRemove = Array.from(new Set(gradeBlocksToRemove.map((block) => block.block_id)));
					const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, block_ids:blockIdsToRemove};
					const apiName = process.env.REACT_APP_ELEM_ENDPOINT_NAME;
					const url = '/elementary/delete-block';
					const myInit = {response:true, body:JSON.stringify(data)};
					await API.post(apiName, url, myInit);
				}

				await refreshBlocksFromBackend();
			} catch(e)
			{
				console.log(e.response);
			}
		}
	};

	const saveGradeBlocks = async ({ blockDataCopy, blockDataImmutable, affectedBlocks, blocks: sourceBlocks, courseId }) => {
		if(!selectedGradeTeachers.length) return { handled:true, close:false };

		const selectedTeacherIdsForSave = editorSelectedTeacherIds.filter((teacherId) => gradeTeacherIds.includes(`${teacherId}`));
		const baseBlocksForSave = editorBaseBlocksRef.current || sourceBlocks || blocks;
		const originalMatchingBlocks = (blockDataImmutable && blockDataImmutable.block_id) ? getMatchingGradeBlocks(blockDataImmutable, baseBlocksForSave, gradeTeacherIds) : [];
		if(selectedTeacherIdsForSave.length === 0)
		{
			if(originalMatchingBlocks.length === 0) return { handled:true, close:true };

			try {
				const deleteData = {
					school_id: schoolInfo.school_id,
					schedule_version_id: schoolInfo.current_schedule_version_id,
					block_ids: Array.from(new Set(originalMatchingBlocks.map((block) => `${block.block_id}`)))
				};
				const apiName = process.env.REACT_APP_ELEM_ENDPOINT_NAME;
				await API.post(apiName, '/elementary/delete-block', {response:true, body:JSON.stringify(deleteData)});
				const refreshedBlocks = await getData('blocks', '/elementary/get-blocks', {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id}, true, 'elementary');
				editorSaveCommittedRef.current = true;
				setBlocks(refreshedBlocks);
				return { handled:true, close:true };
			} catch(e)
			{
				console.log(e.response);
				await swal({
					title: 'Unable to delete block',
					text: e && e.response && e.response.data && e.response.data.msg ? e.response.data.msg : 'Please try again.',
					icon: 'error',
					button: {
						text: 'Okay',
						className: 'blue-btn'
					}
				});
				return { handled:true, close:false };
			}
		}

		const selectedTeacherIdSetForSave = new Set(selectedTeacherIdsForSave.map((teacherId) => `${teacherId}`));
		const desiredDayNumbers = Array.from(new Set(affectedBlocks.map((affectedBlock) => `${affectedBlock.day_number}`)));
		const originalBlocksByTeacherId = originalMatchingBlocks.reduce((results, block) => {
			const teacherId = `${block.homeroom_teacher_id || block.teacher_id || ''}`;
			if(!results.has(teacherId)) results.set(teacherId, []);
			results.get(teacherId).push(block);
			return results;
		}, new Map());
		const blockIdsToDelete = new Set();
		const apiName = process.env.REACT_APP_ELEM_ENDPOINT_NAME;
		const url = '/elementary/save-block';

		try {
			for(const teacher of selectedGradeTeachers)
			{
				const teacherId = `${teacher.teacher_id}`;
				const teacherOriginalBlocks = originalBlocksByTeacherId.get(teacherId) || [];
				const teacherBlockIds = Array.from(new Set(teacherOriginalBlocks.map((block) => `${block.block_id}`)));

				if(!selectedTeacherIdSetForSave.has(teacherId) || desiredDayNumbers.length === 0)
				{
					teacherBlockIds.forEach((blockId) => blockIdsToDelete.add(blockId));
					continue;
				}

				const primaryBlockId = teacherBlockIds[0] || createUniqueObjectID(5);
				const primaryBlockEntries = teacherOriginalBlocks.filter((block) => `${block.block_id}` === `${primaryBlockId}`);
				teacherBlockIds.slice(1).forEach((blockId) => blockIdsToDelete.add(blockId));

				const existingDayEntries = primaryBlockEntries.map((block) => ({
					block_id: primaryBlockId,
					block_day_id: block.block_day_id || null,
					day_number: `${block.day_number}`,
				}));
				const existingDayMap = new Map(existingDayEntries.map((entry) => [`${entry.day_number}`, entry]));
				const affectedGradeBlocks = desiredDayNumbers.map((dayNumber) => {
					const existingEntry = existingDayMap.get(`${dayNumber}`);
					return existingEntry || {
						block_id: primaryBlockId,
						block_day_id: null,
						day_number: `${dayNumber}`,
					};
					});
					const removedGradeBlocks = existingDayEntries.filter((entry) => !desiredDayNumbers.includes(`${entry.day_number}`));
					const classroomId = selectedTeacherIdsForSave.length === 1 ? (blockDataCopy.classroom_id || null) : null;
					const studentList = createStudentIdsForTeacher(teacherId);

				const data = {
					school_id: schoolInfo.school_id,
					schedule_version_id: schoolInfo.current_schedule_version_id,
					affected_blocks: affectedGradeBlocks,
					removed_blocks: removedGradeBlocks,
					block_data: {
						...blockDataCopy,
						block_id: primaryBlockId,
						classroom_id: classroomId,
						course_id: courseId,
						homeroom_teacher_id: teacherId,
						teacher_id: teacherId,
						is_pullout: '0',
						is_special: '0',
						student_list: studentList,
						secondary_teachers: [],
					}
				};
				const myInit = {response:true, body:JSON.stringify(data)};
				await API.post(apiName, url, myInit);
			}

			if(blockIdsToDelete.size > 0)
			{
				const deleteData = {
					school_id: schoolInfo.school_id,
					schedule_version_id: schoolInfo.current_schedule_version_id,
					block_ids: Array.from(blockIdsToDelete)
				};
				await API.post(apiName, '/elementary/delete-block', {response:true, body:JSON.stringify(deleteData)});
			}

			const refreshedBlocks = await getData('blocks', '/elementary/get-blocks', {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id}, true, 'elementary');
			editorSaveCommittedRef.current = true;
			setBlocks(refreshedBlocks);
			return { handled:true, close:true };
		} catch(e)
		{
			console.log(e.response);
			await swal({
				title: 'Unable to save block',
				text: e && e.response && e.response.data && e.response.data.msg ? e.response.data.msg : 'Please try again.',
				icon: 'error',
				button: {
					text: 'Okay',
					className: 'blue-btn'
				}
			});
			return { handled:true, close:false };
		}
	};

	useEffect(() => {
		const numDaysInCycle = scheduleInfo.num_days_in_cycle;
		const nextDays = [];
		for(let day = 1; day <= numDaysInCycle; day++) nextDays.push(`${day}`);
		setDays(nextDays);
	}, [scheduleInfo]);

	useEffect(() => {
		if(!selectedGradeId && gradeOptions.length > 0) setSelectedGradeId(`${gradeOptions[0].value}`);
	}, [gradeOptions, selectedGradeId]);

	useEffect(() => {
		if(gradeTeacherIds.length === 0)
		{
			setSelectedTeacherIds([]);
			setActiveTeacherId(null);
			return;
		}
		setSelectedTeacherIds([...gradeTeacherIds]);
		setActiveTeacherId((currentTeacherId) => {
			if(currentTeacherId && gradeTeacherIds.includes(`${currentTeacherId}`)) return `${currentTeacherId}`;
			return gradeTeacherIds[0] || null;
		});
	}, [gradeTeacherIds, selectedGradeId]);

	useEffect(() => {
		if(selectedTeacherIds.length > 0) return;
		if(activeTeacherId !== null) setActiveTeacherId(null);
	}, [activeTeacherId, selectedTeacherIds]);

	useEffect(() => {
		if(!blockData)
		{
			editorSelectionSeedRef.current = null;
			setEditorSelectedTeacherIds([]);
			return;
		}

		if(gradeTeacherIds.length === 0)
		{
			editorSelectionSeedRef.current = null;
			setEditorSelectedTeacherIds([]);
			return;
		}

		const currentEditorSelectionSeed = `${blockEditorSeedKey || 'none'}::${gradeTeacherIds.join('|')}`;
		if(editorSelectionSeedRef.current === currentEditorSelectionSeed) return;
		editorSelectionSeedRef.current = currentEditorSelectionSeed;

		if(blockData.new || !blockData.block_id)
		{
			setEditorSelectedTeacherIds([...gradeTeacherIds]);
			return;
		}

		const matchingTeacherIds = getMatchingGradeTeacherIds(blockData);
		const fallbackTeacherId = `${blockData.homeroom_teacher_id || blockData.teacher_id || previewTeacherId || ''}`.trim();
		const nextTeacherIds = matchingTeacherIds.length > 0 ? matchingTeacherIds : (fallbackTeacherId ? [fallbackTeacherId] : []);
		setEditorSelectedTeacherIds(nextTeacherIds);
	}, [blockData, blockEditorSeedKey, getMatchingGradeTeacherIds, gradeTeacherIds, previewTeacherId]);

	useEffect(() => {
		if(!periodsLoading && !initialHomeroomGroupsLoading) setIsLoading(false);
	}, [initialHomeroomGroupsLoading, periodsLoading]);

	useEffect(() => {
		const hadOpenSidebar = !!previousBlockDataRef.current;
		const hasOpenSidebar = !!blockData;

		if(hadOpenSidebar && !hasOpenSidebar)
		{
			if(!editorSaveCommittedRef.current && editorBaseBlocksRef.current)
			{
				setBlocks(cloneBlocks(editorBaseBlocksRef.current));
			}
			editorBaseBlocksRef.current = null;
			editorSaveCommittedRef.current = false;
			setEditorPreviewState(null);
		}

		previousBlockDataRef.current = blockData;
	}, [blockData, cloneBlocks, setBlocks]);

	const selectedTeacherHeading = selectedTeacherCount === 0 ? 'Select Teachers' : `${selectedTeacherCount} ${selectedTeacherCount === 1 ? 'Teacher' : 'Teachers'} Selected`;
	const comparisonHelperText = isComparisonMode ? 'Matching course/time blocks merge together. Each block shows how many selected homerooms have it and which teacher schedules are included.' : 'Each block shows which selected homeroom schedules include it.';
	const isEditorOpen = !!blockData;
	const editorAssignmentButtonLabel = `Assign to ${editorSelectedTeacherCount} ${editorSelectedTeacherCount === 1 ? 'teacher' : 'teachers'}`;
	const renderComparisonTab = (block) => {
		const comparisonTeacherIds = Array.isArray(block.comparison_teacher_ids) ? block.comparison_teacher_ids : [];
		if(comparisonTeacherIds.length === 0 || selectedTeacherCount === 0) return null;

		return (
			<div className='grade-blocks-block-tab'>
				<div className='grade-blocks-block-tab-pill-row' style={{ '--grade-block-pill-count':comparisonTeacherIds.length }}>
					{comparisonTeacherIds.map((teacherId) => {
						const teacher = teacherById.get(`${teacherId}`) || null;
						if(!teacher) return null;

						return (
							<div
								className='grade-blocks-block-tab-pill tooltip'
								data-tooltip-text={displayTeacherName(teacher)}
								key={`${block.comparison_signature || block.block_id}-tab-pill-${teacherId}`}
								style={{ backgroundColor:getTeacherTagColorHex(teacher) }}
							/>
						);
					})}
				</div>
				<div className='grade-blocks-block-tab-counter'>
					{block.comparison_teacher_badge || `${comparisonTeacherIds.length}/${selectedTeacherCount}`}
				</div>
			</div>
		);
	};
	const renderComparisonMembership = (block) => {
		const comparisonTeacherIds = Array.isArray(block.comparison_teacher_ids) ? block.comparison_teacher_ids : [];
		if(comparisonTeacherIds.length === 0 || selectedTeacherCount === 0) return null;

		return (
			<div className='grade-blocks-block-membership-popover'>
				<div className='grade-blocks-block-membership-popover-title'>
					{comparisonTeacherIds.length} of {selectedTeacherCount} selected homerooms
				</div>
				<div className='grade-blocks-block-chip-list'>
					{comparisonTeacherIds.map((teacherId) => {
						const teacher = teacherById.get(`${teacherId}`) || null;
						if(!teacher) return null;
						return (
							<div className='grade-blocks-block-chip-list-item' key={`${block.comparison_signature || block.block_id}-teacher-row-${teacherId}`}>
								<div className='grade-blocks-block-popover-chip' style={{ backgroundColor:getTeacherTagColorHex(teacher) }}/>
								<div className='grade-blocks-block-chip-name'>{displayTeacherName(teacher)}</div>
							</div>
						);
					})}
				</div>
			</div>
		);
	};
	const renderUnderlayMembership = (block) => {
		const homeroomTeacher = teacherById.get(`${block.homeroom_teacher_id || block.teacher_id || ''}`) || null;
		const teachingTeacher = teacherById.get(`${block.teacher_id || ''}`) || null;
		const homeroomTeacherName = displayTeacherName(homeroomTeacher);
		const teachingTeacherName = displayTeacherName(teachingTeacher);
		const blockName = getGradeBlockName(block, courses);
		if(!homeroomTeacherName && !teachingTeacherName) return null;

		return (
			<div className='grade-blocks-block-membership-popover'>
				<div className='grade-blocks-block-membership-popover-title'>{blockName}</div>
				<div className='grade-blocks-block-chip-list'>
					{homeroomTeacherName && (
						<div className='grade-blocks-block-chip-list-item'>
							<div className='grade-blocks-block-popover-chip' style={{ backgroundColor:getTeacherTagColorHex(homeroomTeacher) }}/>
							<div className='grade-blocks-block-chip-name'>Homeroom: {homeroomTeacherName}</div>
						</div>
					)}
					{teachingTeacherName && teachingTeacherName !== homeroomTeacherName && (
						<div className='grade-blocks-block-chip-list-item'>
							<div className='grade-blocks-block-popover-chip' style={{ backgroundColor:getTeacherTagColorHex(teachingTeacher) }}/>
							<div className='grade-blocks-block-chip-name'>Teacher: {teachingTeacherName}</div>
						</div>
					)}
				</div>
			</div>
		);
	};
	const toggleEditorTeacherSelection = (teacherId, markChanged = () => {}, isLocked = false) => {
		if(isLocked) return;
		setEditorSelectedTeacherIds((currentTeacherIds) => {
			const normalizedTeacherId = `${teacherId}`;
			const nextTeacherIds = currentTeacherIds.includes(normalizedTeacherId)
				? currentTeacherIds.filter((tempTeacherId) => tempTeacherId !== normalizedTeacherId)
				: [...currentTeacherIds, normalizedTeacherId];
			return nextTeacherIds;
		});
		markChanged();
	};

	return (
		<>
		{subscreenRestricted ? (
			<div className='general-screen-message'>
				<FontAwesomeIcon className='dark-blue-text' style={{fontSize:'60px'}} icon={faLock}/>
				<h1>No Access</h1>
				<div>You do not have access to this screen</div>
			</div>
		) : subscreenLocked ? (
			<div className='general-screen-message'>
				<FontAwesomeIcon className='dark-blue-text' style={{fontSize:'60px'}} icon={faLock}/>
				<h1>Temporarily Locked</h1>
				<div>This screen is locked until all other prior steps are completed</div>
			</div>
		) : isLoading ? (
			<div className='center-text'>
				<img src={require('../../../images/balls.gif')} alt='Loading...' style={{height:'25px'}}/>
			</div>
		) : (
			<>
				<EditBlock schoolInfo={schoolInfo} days={days} blockData={blockData} blocks={blocks} setBlocks={setBlocks} courses={courses} setCourses={setCourses} teachers={selectedTeacher ? [selectedTeacher] : []} classrooms={editorHasSingleTeacherSelection ? editorSelectedTeacherClassrooms : []} students={selectedTeacherStudents} overlapCheck={timesOverlap} removeBlock={removeBlock} toggleEditBlock={toggleEditBlock} sidePanelClosing={sidePanelClosing} setSidePanelClosing={setSidePanelClosing} saveBlockOverride={saveGradeBlocks} onEditorStateChange={setEditorPreviewState} allowedBlockTypes={['block', 'lunch']} showClassroomField={editorHasSingleTeacherSelection} saveButtonLabel={editorAssignmentButtonLabel} renderBlockTypeExtras={({ markChanged, isLocked }) => (
					<div className='grade-blocks-editor-pill-section'>
						<div className='elem-edit-block-label'>Homeroom Teachers</div>
						<div className='grade-blocks-selection-caption grade-blocks-editor-selection-caption'>Which homerooms should this apply to?</div>
						<div className='grade-blocks-teacher-pill-row grade-blocks-editor-pill-row'>
							{selectedGradeTeachers.map((teacher) => (
								<div
									className={`grade-blocks-teacher-pill grade-blocks-teacher-pill-with-checkbox grade-blocks-teacher-pill-colorized ${editorSelectedTeacherIdSet.has(`${teacher.teacher_id}`) ? 'grade-blocks-teacher-pill-selected' : 'grade-blocks-teacher-pill-unselected'} ${isLocked ? 'opaque' : ''}`}
									key={`editor-teacher-${teacher.teacher_id}`}
									onClick={() => toggleEditorTeacherSelection(`${teacher.teacher_id}`, markChanged, isLocked)}
									style={getTeacherPillStyle(teacher, editorSelectedTeacherIdSet.has(`${teacher.teacher_id}`))}
								>
									<span className='grade-blocks-teacher-pill-checkbox'>
										<FontAwesomeIcon icon={editorSelectedTeacherIdSet.has(`${teacher.teacher_id}`) ? faCheckSquare : faSquare}/>
									</span>
									<span className='grade-blocks-teacher-pill-label'>{displayTeacherName(teacher)}</span>
								</div>
							))}
						</div>
					</div>
				)}/>
				<div className='mb-elem-container' style={isEditorOpen ? { pointerEvents:'none' } : undefined}>
					<div className='mb-elem-top-bar'>
						<div className='mb-top-bar-inner'>
							<div className='mb-elem-schedule-name-container'>
								<h1>Grade Blocks</h1>
							</div>
							<p className='progress-subheader elem-manage-homerooms-subheader'>Build one schedule, then quickly apply it to all teachers in the grade. You can always edit individual teacher's schedules later.</p>
							<div className='grade-blocks-selector-row'>
								<div className='grade-blocks-selector-control'>
									<div className='specials-setup-label'>Grade</div>
									<Dropdown data={gradeOptions} currentValue={selectedGradeId} displayDefault='Select Grade' handleChange={setSelectedGradeId}/>
								</div>
							</div>
						</div>
					</div>
					{!initialHomeroomGroupsLoading && homeroomGroups.length === 0 ? (
						<div className='general-screen-message' style={{padding:'30px 20px'}}>
							<h2>No Homeroom Grades Yet</h2>
							<p>Set up your homeroom grade groups first so this screen knows which teachers belong in each grade-level schedule.</p>
						</div>
					) : (
						<div className='mb-elem-teacher-schedule grade-blocks-magnetboard'>
							<div className='mb-elem-teacher-row'>
								<div className='mb-elem-teacher-info'>
									<h1 className='mb-elem-teacher-name'>{selectedTeacherHeading}</h1>
									<div className='grade-blocks-legend-row'>
										<div className={`grade-blocks-teacher-pill grade-blocks-teacher-pill-with-checkbox grade-blocks-teacher-pill-legend ${allGradeTeachersSelected ? 'grade-blocks-teacher-pill-selected' : ''}`} onClick={handleSelectAllTeachers}>
											<span className='grade-blocks-teacher-pill-checkbox'>
												<FontAwesomeIcon icon={allGradeTeachersSelected ? faCheckSquare : faSquare}/>
											</span>
											<span className='grade-blocks-teacher-pill-label'>All Teachers</span>
										</div>
										{selectedGradeTeachers.map((teacher) => (
											<div
												className={`grade-blocks-teacher-pill grade-blocks-teacher-pill-with-checkbox grade-blocks-teacher-pill-colorized grade-blocks-teacher-pill-legend ${selectedTeacherIdSet.has(`${teacher.teacher_id}`) ? 'grade-blocks-teacher-pill-selected' : 'grade-blocks-teacher-pill-unselected'}`}
												key={`legend-${teacher.teacher_id}`}
												onClick={() => handleToggleTeacherSelection(`${teacher.teacher_id}`)}
												style={getTeacherPillStyle(teacher, selectedTeacherIdSet.has(`${teacher.teacher_id}`))}
											>
												<span className='grade-blocks-teacher-pill-checkbox'>
													<FontAwesomeIcon icon={selectedTeacherIdSet.has(`${teacher.teacher_id}`) ? faCheckSquare : faSquare}/>
												</span>
												<span className='grade-blocks-teacher-pill-label'>{displayTeacherName(teacher)}</span>
											</div>
										))}
									</div>
									<div className='grade-blocks-selection-caption'>{comparisonHelperText}</div>
								</div>
							</div>
							{selectedTeacherCount === 0 || !selectedTeacher ? (
								<div className='general-screen-message' style={{padding:'30px 20px'}}>
									<h2>Select At Least One Homeroom Teacher</h2>
									<p>Choose one or more teacher pills above to compare the grade-level schedule.</p>
								</div>
							) : (
								<ScheduleGrid
									className='grade-blocks-schedule-grid'
									days={days}
									getDayKey={(dayNumber) => `${selectedTeacherIds.join('-') || 'none'}-${previewTeacherId || 'preview'}-${dayNumber}`}
									getDayLabel={(dayNumber) => `Day ${dayNumber}`}
									getDayItems={buildComparisonDayBlocks}
									getDayFloatingItems={buildComparisonFloatingBlocks}
									getDayOverlays={buildComparisonUnderlayBlocks}
									onDayBodyClick={isEditorOpen ? null : ((dayNumber, { clickTimeRange }) => previewTeacherId && createNewBlock(previewTeacherId, dayNumber, clickTimeRange))}
									renderOverlay={(block, { day, dayOverlays, loggedPositions }) => {
										const blockName = getGradeBlockName(block, courses);
										const blockColor = getGradeBlockColor(block);
										const underlayMembership = renderUnderlayMembership(block);
										const rawBlockPosition = calculateTimeGridColumnStartEnd(block, dayOverlays, loggedPositions, block.start_time, block.end_time, 'block_id');
										const blockPosition = tightenGradeBlockPosition(rawBlockPosition);
										loggedPositions[block.block_id] = blockPosition.position_index;

										return (
											<ScheduleGridItem
												key={`${block.block_id}-${block.block_day_id || block.day_number || 'day'}-underlay`}
												itemKey={`${block.block_id}-underlay`}
												position={blockPosition}
												rowStart={block.row_start}
												rowEnd={block.row_end}
												color={blockColor}
												name={blockName}
												nameTooltipText={`${blockName} • Context only`}
												time={`${formatTime(block.start_time)} - ${formatTime(block.end_time)}`}
												timeTooltipText={`${formatTime(block.start_time)} - ${formatTime(block.end_time)}`}
												hoverPopoverContent={underlayMembership}
												className='grade-blocks-underlay-block'
											/>
										);
									}}
									renderItem={(block, { day, dayItems, loggedPositions }) => {
										const dayConflictKeys = getCurrentDayConflictKeys(day);
										const blockName = getGradeBlockName(block, courses);
										const blockColor = getGradeBlockColor(block);
										const rawBlockPosition = calculateTimeGridColumnStartEnd(block, dayItems, loggedPositions, block.start_time, block.end_time, 'block_id');
										const blockPosition = tightenGradeBlockPosition(rawBlockPosition);
										const blockColumnSpan = Math.max(0, (parseInt(blockPosition.col_end, 10) || 0) - (parseInt(blockPosition.col_start, 10) || 0));
										const blockEditable = isGradeManagedBlock(block);
										const hideTimeLabel = blockColumnSpan <= 18;
										const formattedTimeRange = `${formatTime(block.start_time)} - ${formatTime(block.end_time)}`;
										loggedPositions[block.block_id] = blockPosition.position_index;

										return (
											<ScheduleGridItem
												key={`${block.block_id}-${block.block_day_id || block.day_number || 'day'}`}
												itemKey={block.block_id}
												position={blockPosition}
												rowStart={block.row_start}
												rowEnd={block.row_end}
												color={blockColor}
												temporary={!!block.new}
												conflict={dayConflictKeys.has(`${block.block_day_id || block.block_id}-${day}`)}
												selected={blockData && blockData.block_id === block.block_id}
												onClick={!isEditorOpen && blockEditable ? (() => {
													setActiveTeacherId(`${block.homeroom_teacher_id || block.teacher_id || previewTeacherId}`);
													toggleEditBlock(block.block_id);
												}) : null}
												name={blockName}
												nameTooltipText={block.comparison_teacher_total ? `${blockName} • ${block.comparison_shared_count} of ${block.comparison_teacher_total} selected homerooms` : blockName}
												time={hideTimeLabel ? '' : formattedTimeRange}
												timeTooltipText={hideTimeLabel ? '' : formattedTimeRange}
												headerMenuOptions={(!isEditorOpen && blockEditable && Array.isArray(block.block_days) && block.block_days.length > 1) ? [
													{ label:'Remove This Day', onClick:(e) => removeBlock(block.block_id, e, { scope:'day', dayNumber:block.day_number }) },
													{ label:'Remove All Days', onClick:(e) => removeBlock(block.block_id, e, { scope:'all' }) }
												] : []}
												headerAction={(!isEditorOpen && blockEditable && !(Array.isArray(block.block_days) && block.block_days.length > 1)) ? <FontAwesomeIcon className='mb-elem-remove-block small-text cursor-pointer' icon={faTimes} onClick={(e) => removeBlock(block.block_id, e, { scope:'all' })}/> : null}
												className='grade-blocks-comparison-block'
												topContent={renderComparisonTab(block)}
												hoverPopoverContent={renderComparisonMembership(block)}
											/>
										);
									}}
									renderFloatingItem={(block, { day, dayItems, dayFloatingItems, loggedPositions }) => {
										return null;
									}}
									onAddItem={isEditorOpen ? null : ((dayNumber) => previewTeacherId && createNewBlock(previewTeacherId, dayNumber))}
									addItemLabel='Add Period'
									addItemClassName='small-text'
								/>
							)}
						</div>
					)}
				</div>
				<div className='fixed-bottom-save-container'>
					<Button classes={['btn', 'btn-extra-large', 'green-btn', 'align-right', 'move-to-next-screen-btn']} selected={false} text='Go to Next Step' onClickFunction={handleMoveToNextScreen} />
				</div>
			</>
		)}
		</>
	);
}
